import React, { useState, useEffect, useMemo } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import type { DashboardStats } from '../../services/adminApiService';
import './AdminComponents.css';

// NOTE: Previously used a mock reportsService here. We now use real APIs from useAdminApiService.

const ReportsAnalytics: React.FC = () => {
  const adminApiService = useAdminApiService();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading] = useState(false);
  const [selectedReport, setSelectedReport] = useState<'overview'|'kyc'|'kyb'>('overview');
  const [dateRange, setDateRange] = useState('30');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [productId, setProductId] = useState<string | undefined>(undefined);
  const [products, setProducts] = useState<Array<{id:string; name:string}>>([]);
  const [kycSummary, setKycSummary] = useState<any | null>(null);
  const [kybSummary, setKybSummary] = useState<any | null>(null);
  const api = useAdminApiService();
  const { getKycSubmissions, getKybSubmissions, getAvailableProducts } = api;

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await adminApiService.getDashboardStats();
        if (response.success && response.data) {
          setStats(response.data as DashboardStats);
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
      }
    };
    loadStats();
  }, [adminApiService]);

  // Load products for filter (match Compliance page) with profile fallback
  useEffect(() => {
    (async () => {
      try {
        console.log('DEBUG: Loading products for selectedReport:', selectedReport);
        let mapped: Array<{ id: string; name: string }> = [];
        const res = await getAvailableProducts();
        console.log('DEBUG: getAvailableProducts response:', res);
        
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          mapped = res.data.map((p: any) => ({ id: String(p.id ?? p.productId ?? p), name: p.name ?? p.productName ?? String(p), customerType: p.customerType ?? p.type }));
          console.log('DEBUG: Mapped products from getAvailableProducts:', mapped);
        } else {
          const profile = await api.getAdminProfile();
          const ap = profile?.data?.assignedProducts || [];
          console.log('DEBUG: Assigned products from profile:', ap);
          if (Array.isArray(ap)) {
            mapped = ap.map((p: any) => ({ id: String(p.id ?? p.productId ?? p), name: p.name ?? p.productName ?? String(p), customerType: p.customerType ?? p.type }));
            console.log('DEBUG: Mapped products from profile:', mapped);
          }
        }
        // Filter by customer type to match tab
        const filtered = mapped.filter((p: any) => {
          if (selectedReport === 'kyc') return (p.customerType === 'PERSONAL');
          if (selectedReport === 'kyb') return (p.customerType === 'BUSINESS');
          return true;
        }).map(({ id, name }) => ({ id, name }));

        console.log('DEBUG: Filtered products for', selectedReport, ':', filtered);
        setProducts(filtered);
        // Always reset productId when tab changes to ensure correct product type
        if (filtered.length > 0) {
          console.log('DEBUG: Setting productId to:', filtered[0].id);
          setProductId(filtered[0].id);
        } else {
          console.log('DEBUG: No products available, clearing productId');
          setProductId(undefined);
        }
      } catch (e) {
        console.error('DEBUG: Failed to load products for reports filter', e);
      }
    })();
  }, [getAvailableProducts, api, selectedReport]);

  // Permission gating: read from admin profile
  const [canViewKyc, setCanViewKyc] = useState<boolean>(false);
  const [canViewKyb, setCanViewKyb] = useState<boolean>(false);
  const [canViewOverview, setCanViewOverview] = useState<boolean>(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const profile = await api.getAdminProfile();
        const perms: string[] = profile?.data?.permissions || [];
        console.log('DEBUG: Profile loaded, permissions:', perms);
        console.log('DEBUG: User role:', profile?.data?.role);
        
        const canViewKycFlag = perms.includes('VIEW_KYC_REPORTS') || perms.includes('VIEW_COMPLIANCE_ALERTS');
        const canViewKybFlag = perms.includes('VIEW_KYB_REPORTS') || perms.includes('VIEW_COMPLIANCE_ALERTS');
        
        console.log('DEBUG: Setting permissions - canViewKyc:', canViewKycFlag, 'canViewKyb:', canViewKybFlag);
        
        setCanViewOverview(perms.includes('VIEW_ADMIN_REPORTS'));
        setCanViewKyc(canViewKycFlag);
        setCanViewKyb(canViewKybFlag);
        setIsSuperAdmin((profile?.data?.role || '') === 'SUPER_ADMIN');
        // If current tab is not allowed, switch to first allowed
        if (!canViewOverview && selectedReport === 'overview') setSelectedReport('kyc');
      } catch (e) {
        console.error('DEBUG: Error loading profile:', e);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const filters = useMemo(() => ({ dateRange, status: statusFilter }), [dateRange, statusFilter]);

  // Generate summaries using the same endpoints as Compliance, filtered by product
  const generateKycSummary = async () => {
    const selectedPid = productId ? Number(productId) : undefined;
    const totalRes = await getKycSubmissions(0, 1, undefined, selectedPid);
    const next: any = {
      total: totalRes?.data?.totalElements ?? 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      inReview: 0,
      today: 0
    };
    const wanted = new Set(statusFilter.length ? statusFilter : []);
    const calls: Array<Promise<any>> = [];
    if (wanted.has('PENDING')) calls.push(getKycSubmissions(0, 1, 'PENDING', selectedPid).then(r=> next.pending = r?.data?.totalElements ?? 0));
    if (wanted.has('APPROVED')) calls.push(getKycSubmissions(0, 1, 'APPROVED', selectedPid).then(r=> next.approved = r?.data?.totalElements ?? 0));
    if (wanted.has('REJECTED')) calls.push(getKycSubmissions(0, 1, 'REJECTED', selectedPid).then(r=> next.rejected = r?.data?.totalElements ?? 0));
    if (wanted.has('IN_REVIEW')) calls.push(getKycSubmissions(0, 1, 'IN_REVIEW', selectedPid).then(r=> next.inReview = r?.data?.totalElements ?? 0));
    await Promise.all(calls);
    setKycSummary(next);
  };

  const generateKybSummary = async () => {
    console.log('DEBUG: generateKybSummary called with productId:', productId);
    const selectedPid = productId ? Number(productId) : undefined;
    console.log('DEBUG: selectedPid for KYB:', selectedPid);
    
    const totalRes = await getKybSubmissions(0, 1, undefined, selectedPid);
    console.log('DEBUG: KYB totalRes:', totalRes);
    console.log('DEBUG: KYB totalRes.data:', totalRes?.data);
    console.log('DEBUG: KYB totalRes.data.totalElements:', totalRes?.data?.totalElements);
    
    const next: any = {
      total: totalRes?.data?.totalElements ?? 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      inReview: 0,
      today: 0
    };
    const wanted = new Set(statusFilter.length ? statusFilter : []);
    const calls: Array<Promise<any>> = [];
    if (wanted.has('PENDING')) calls.push(getKybSubmissions(0, 1, 'PENDING', selectedPid).then(r=> next.pending = r?.data?.totalElements ?? 0));
    if (wanted.has('APPROVED')) calls.push(getKybSubmissions(0, 1, 'APPROVED', selectedPid).then(r=> next.approved = r?.data?.totalElements ?? 0));
    if (wanted.has('REJECTED')) calls.push(getKybSubmissions(0, 1, 'REJECTED', selectedPid).then(r=> next.rejected = r?.data?.totalElements ?? 0));
    if (wanted.has('IN_REVIEW')) calls.push(getKybSubmissions(0, 1, 'IN_REVIEW', selectedPid).then(r=> next.inReview = r?.data?.totalElements ?? 0));
    await Promise.all(calls);
    console.log('DEBUG: KYB summary calculated:', next);
    setKybSummary(next);
  };

  useEffect(() => {
    (async () => {
      console.log('DEBUG: useEffect triggered with:', { selectedReport, productId, canViewKyc, canViewKyb });
      if (selectedReport === 'kyc' && canViewKyc) {
        console.log('DEBUG: Generating KYC summary');
        await generateKycSummary();
      }
      if (selectedReport === 'kyb' && canViewKyb) {
        console.log('DEBUG: Generating KYB summary');
        await generateKybSummary();
      }
    })();
  }, [selectedReport, productId, canViewKyc, canViewKyb]);

  const handleExportReport = async (reportType: string) => {
    try {
      // Simulate export functionality
      const fileName = `${reportType}_report_${new Date().toISOString().split('T')[0]}.csv`;
      alert(`Export initiated: ${fileName}\n\nNote: This is a demo. In production, this would download a real CSV/PDF file.`);
    } catch (error) {
      alert('Export failed. Please try again.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const buildConicGradient = (parts: Array<{ value: number; color: string }>) => {
    const total = Math.max(parts.reduce((s, p) => s + (p.value || 0), 0), 1);
    let current = 0;
    const stops: string[] = [];
    parts.forEach(p => {
      const start = (current / total) * 360;
      const end = ((current + (p.value || 0)) / total) * 360;
      stops.push(`${p.color} ${start}deg ${end}deg`);
      current += (p.value || 0);
    });
    return `conic-gradient(${stops.join(',')})`;
  };

  if (loading) {
    return (
      <div className="admin-component-loading">
        <div className="loading-spinner"></div>
        <p>Loading reports...</p>
      </div>
    );
  }

  return (
    <div className="reports-analytics">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h2>📊 Reports & Analytics</h2>
          <p>Generate reports and view system analytics</p>
        </div>
        <div className="dashboard-actions">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              marginRight: '12px'
            }}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button 
            className="refresh-btn" 
            onClick={() => adminApiService.getDashboardStats().then(response => {
              if (response.success && response.data) {
                setStats(response.data as DashboardStats);
              }
            })}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: '#3b82f6',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Report Navigation */}
      <div className="report-nav" style={{ 
        marginBottom: '24px', 
        background: '#fff', 
        padding: '16px', 
        borderRadius: '12px', 
        border: '1px solid #e5e7eb' 
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {canViewOverview && (
            <button onClick={() => setSelectedReport('overview')} className={selectedReport==='overview'?'tab-btn active':'tab-btn'}>📈 Overview</button>
          )}
          {canViewKyc && (
            <button onClick={() => setSelectedReport('kyc')} className={selectedReport==='kyc'?'tab-btn active':'tab-btn'}>🧾 Enhanced KYC</button>
          )}
          {canViewKyb && (
            <button onClick={() => setSelectedReport('kyb')} className={selectedReport==='kyb'?'tab-btn active':'tab-btn'}>🏢 Enhanced KYB</button>
          )}
        </div>
        {/* Minimal filter panel */}
        <div style={{ display:'flex', gap:12, marginTop:12, alignItems:'center', flexWrap:'wrap' }}>
          <select value={dateRange} onChange={e=>setDateRange(e.target.value)} style={{ padding:'8px 12px', border:'1px solid #d1d5db', borderRadius:6 }}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <select multiple value={statusFilter} onChange={e=>setStatusFilter(Array.from(e.target.selectedOptions).map(o=>o.value))} style={{ padding:'8px 12px', border:'1px solid #d1d5db', borderRadius:6, minWidth:160 }}>
            <option value="PENDING">PENDING</option>
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
          </select>
          {(selectedReport==='kyc' || selectedReport==='kyb') && (
            <>
              {/* Product filter to match Compliance page */}
              <label style={{ fontSize:12, color:'#6b7280' }}>Filter by Product:&nbsp;</label>
              <select
                value={productId ?? (canViewOverview || isSuperAdmin ? 'ALL' : '')}
                onChange={(e)=> setProductId(e.target.value === 'ALL' ? undefined : (e.target.value || undefined))}
                style={{ padding:'8px 12px', border:'1px solid #d1d5db', borderRadius:6, minWidth: 280 }}
              >
                {(canViewOverview || isSuperAdmin) && (
                  <option value="ALL">All products</option>
                )}
                {products.length > 0 ? (
                  products.map(p => (
                    <option key={p.id} value={String(p.id)}>{p.name}</option>
                  ))
                ) : (
                  <option value="" disabled>No products available</option>
                )}
              </select>
              <button className="action-btn primary" onClick={() => {
                if (selectedReport==='kyc') generateKycSummary(); else generateKybSummary();
              }}>Generate</button>
              <button className="action-btn secondary" onClick={async ()=> {
                alert('Export will be wired to backend export endpoints (customers/transactions/compliance) upon confirmation.');
              }}>Export</button>
            </>
          )}
        </div>
      </div>

      {/* Overview Report */}
      {selectedReport === 'overview' && stats && (
        <div className="overview-report">
          {/* Key Metrics */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '20px', 
            marginBottom: '24px' 
          }}>
            <div className="metric-card" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Total Customers</h3>
              <p style={{ margin: '0', fontSize: '32px', fontWeight: 'bold' }}>{stats.totalCustomers}</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                {stats.activeCustomers} active ({Math.round(stats.activeCustomers / stats.totalCustomers * 100)}%)
              </p>
            </div>

            <div className="metric-card" style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Total Balance</h3>
              <p style={{ margin: '0', fontSize: '32px', fontWeight: 'bold' }}>{formatCurrency(stats.totalBalance)}</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                Across {stats.totalAccounts} accounts
              </p>
            </div>

            <div className="metric-card" style={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Today's Transactions</h3>
              <p style={{ margin: '0', fontSize: '32px', fontWeight: 'bold' }}>{stats.todayTransactions}</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                {stats.pendingTransactions} pending approval
              </p>
            </div>

            <div className="metric-card" style={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>System Health</h3>
              <p style={{ margin: '0', fontSize: '32px', fontWeight: 'bold' }}>{stats.systemHealth}</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                {stats.openAlerts} open alerts
              </p>
            </div>
          </div>

          {/* Charts Section */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
            gap: '24px', 
            marginBottom: '24px' 
          }}>
            {/* Account Status Chart */}
            <div style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '12px', 
              border: '1px solid #e5e7eb' 
            }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#374151' }}>Account Status Distribution</h3>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '200px', 
                  height: '200px', 
                  margin: '0 auto 20px', 
                  background: `conic-gradient(
                    #10b981 0deg ${stats.activeAccounts / stats.totalAccounts * 360}deg,
                    #ef4444 ${stats.activeAccounts / stats.totalAccounts * 360}deg 360deg
                  )`,
                  borderRadius: '50%',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    background: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#374151' }}>
                      {stats.totalAccounts}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Total</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <div style={{ width: '12px', height: '12px', background: '#10b981', borderRadius: '50%' }}></div>
                      <span style={{ fontSize: '14px', color: '#374151' }}>Active</span>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>{stats.activeAccounts}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <div style={{ width: '12px', height: '12px', background: '#ef4444', borderRadius: '50%' }}></div>
                      <span style={{ fontSize: '14px', color: '#374151' }}>Frozen</span>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ef4444' }}>{stats.frozenAccounts}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Trends */}
            <div style={{ 
              background: 'white', 
              padding: '24px', 
              borderRadius: '12px', 
              border: '1px solid #e5e7eb' 
            }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#374151' }}>Transaction Overview</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>Completed</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '100px',
                      height: '8px',
                      background: '#e5e7eb',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${stats.completedTransactions / (stats.completedTransactions + stats.pendingTransactions) * 100}%`,
                        height: '100%',
                        background: '#10b981'
                      }}></div>
                    </div>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{stats.completedTransactions}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>Pending</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '100px',
                      height: '8px',
                      background: '#e5e7eb',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${stats.pendingTransactions / (stats.completedTransactions + stats.pendingTransactions) * 100}%`,
                        height: '100%',
                        background: '#f59e0b'
                      }}></div>
                    </div>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{stats.pendingTransactions}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>Today</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '100px',
                      height: '8px',
                      background: '#e5e7eb',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${Math.min(stats.todayTransactions / 100 * 100, 100)}%`,
                        height: '100%',
                        background: '#3b82f6'
                      }}></div>
                    </div>
                    <span style={{ fontWeight: 'bold', color: '#374151' }}>{stats.todayTransactions}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Export Section */}
          <div style={{ 
            background: 'white', 
            padding: '24px', 
            borderRadius: '12px', 
            border: '1px solid #e5e7eb' 
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#374151' }}>Export Reports</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <button
                onClick={() => handleExportReport('overview')}
                style={{
                  padding: '12px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center'
                }}
              >
                📄 Overview Report
              </button>
              <button
                onClick={() => handleExportReport('customers')}
                style={{
                  padding: '12px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center'
                }}
              >
                👥 Customer Report
              </button>
              <button
                onClick={() => handleExportReport('transactions')}
                style={{
                  padding: '12px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center'
                }}
              >
                💳 Transaction Report
              </button>
              <button
                onClick={() => handleExportReport('compliance')}
                style={{
                  padding: '12px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center'
                }}
              >
                ⚠️ Compliance Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced KYC Report */}
      {selectedReport === 'kyc' && (
        <div style={{ background:'white', padding:24, borderRadius:12, border:'1px solid #e5e7eb' }}>
          <h3 style={{ margin:0, marginBottom:16 }}>Enhanced KYC Summary</h3>
          {kycSummary ? (
            <>
              <div className="reports-kpi-grid" style={{ marginBottom: 20 }}>
                <div className="kpi-card kpi--total"><div className="kpi-label">Total</div><div className="kpi-value">{kycSummary.total ?? 0}</div></div>
                <div className="kpi-card kpi--pending"><div className="kpi-label">Pending</div><div className="kpi-value">{kycSummary.pending ?? 0}</div></div>
                <div className="kpi-card kpi--approved"><div className="kpi-label">Approved</div><div className="kpi-value">{kycSummary.approved ?? 0}</div></div>
                <div className="kpi-card kpi--rejected"><div className="kpi-label">Rejected</div><div className="kpi-value">{kycSummary.rejected ?? 0}</div></div>
                <div className="kpi-card kpi--review"><div className="kpi-label">In Review</div><div className="kpi-value">{kycSummary.inReview ?? 0}</div></div>
                <div className="kpi-card kpi--today"><div className="kpi-label">Today</div><div className="kpi-value">{kycSummary.today ?? 0}</div></div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
                {/* Doughnut style distribution chart */}
                <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:16 }}>
                  <h4 style={{ margin:'0 0 12px 0', color:'#374151' }}>KYC Status Distribution</h4>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ width:220, height:220, margin:'0 auto 12px', background: buildConicGradient([
                      { value: kycSummary.approved ?? 0, color: '#10b981' },
                      { value: kycSummary.pending ?? 0, color: '#f59e0b' },
                      { value: kycSummary.rejected ?? 0, color: '#ef4444' },
                      { value: kycSummary.inReview ?? 0, color: '#3b82f6' },
                    ]), borderRadius:'50%', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <div style={{ width:130, height:130, background:'#fff', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column' }}>
                        <div style={{ fontSize:28, fontWeight:800, color:'#374151' }}>{kycSummary.total ?? 0}</div>
                        <div style={{ fontSize:12, color:'#6b7280' }}>Total</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', justifyContent:'center', gap:18 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#10b981', borderRadius:999 }}></span><span style={{ color:'#374151' }}>Approved</span></div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#f59e0b', borderRadius:999 }}></span><span style={{ color:'#374151' }}>Pending</span></div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#ef4444', borderRadius:999 }}></span><span style={{ color:'#374151' }}>Rejected</span></div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#3b82f6', borderRadius:999 }}></span><span style={{ color:'#374151' }}>In Review</span></div>
                    </div>
                  </div>
                </div>

                {/* Horizontal bars */}
                <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:16 }}>
                  <h4 style={{ margin:'0 0 12px 0', color:'#374151' }}>Breakdown</h4>
                  {[
                    { label:'Approved', value: kycSummary.approved ?? 0, color:'#10b981' },
                    { label:'Pending', value: kycSummary.pending ?? 0, color:'#f59e0b' },
                    { label:'Rejected', value: kycSummary.rejected ?? 0, color:'#ef4444' },
                    { label:'In Review', value: kycSummary.inReview ?? 0, color:'#3b82f6' },
                  ].map((row) => (
                    <div key={row.label} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                      <div style={{ width:120, color:'#374151' }}>{row.label}</div>
                      <div style={{ flex:1, height:8, background:'#e5e7eb', borderRadius:6, position:'relative' }}>
                        <div style={{ width: `${(kycSummary.total ? (row.value / (kycSummary.total || 1)) * 100 : 0)}%`, height:8, background: row.color, borderRadius:6 }}></div>
                      </div>
                      <div style={{ width:24, textAlign:'right', color:'#374151' }}>{row.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : 'Generating...'}
        </div>
      )}

      {/* Enhanced KYB Report */}
      {selectedReport === 'kyb' && (
        <div style={{ background:'white', padding:24, borderRadius:12, border:'1px solid #e5e7eb' }}>
          <h3 style={{ margin:0, marginBottom:16 }}>Enhanced KYB Summary</h3>
          {kybSummary ? (
            <>
              <div className="reports-kpi-grid" style={{ marginBottom: 20 }}>
                <div className="kpi-card kpi--total"><div className="kpi-label">Total</div><div className="kpi-value">{kybSummary.total ?? 0}</div></div>
                <div className="kpi-card kpi--pending"><div className="kpi-label">Pending</div><div className="kpi-value">{kybSummary.pending ?? 0}</div></div>
                <div className="kpi-card kpi--approved"><div className="kpi-label">Approved</div><div className="kpi-value">{kybSummary.approved ?? 0}</div></div>
                <div className="kpi-card kpi--rejected"><div className="kpi-label">Rejected</div><div className="kpi-value">{kybSummary.rejected ?? 0}</div></div>
                <div className="kpi-card kpi--review"><div className="kpi-label">In Review</div><div className="kpi-value">{kybSummary.inReview ?? 0}</div></div>
                <div className="kpi-card kpi--today"><div className="kpi-label">Today</div><div className="kpi-value">{kybSummary.today ?? 0}</div></div>
        </div>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
                <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:16 }}>
                  <h4 style={{ margin:'0 0 12px 0', color:'#374151' }}>KYB Status Distribution</h4>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ width:220, height:220, margin:'0 auto 12px', background: buildConicGradient([
                      { value: kybSummary.approved ?? 0, color: '#10b981' },
                      { value: kybSummary.pending ?? 0, color: '#f59e0b' },
                      { value: kybSummary.rejected ?? 0, color: '#ef4444' },
                      { value: kybSummary.inReview ?? 0, color: '#3b82f6' },
                    ]), borderRadius:'50%', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <div style={{ width:130, height:130, background:'#fff', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column' }}>
                        <div style={{ fontSize:28, fontWeight:800, color:'#374151' }}>{kybSummary.total ?? 0}</div>
                        <div style={{ fontSize:12, color:'#6b7280' }}>Total</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', justifyContent:'center', gap:18 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#10b981', borderRadius:999 }}></span><span style={{ color:'#374151' }}>Approved</span></div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#f59e0b', borderRadius:999 }}></span><span style={{ color:'#374151' }}>Pending</span></div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#ef4444', borderRadius:999 }}></span><span style={{ color:'#374151' }}>Rejected</span></div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:10, height:10, background:'#3b82f6', borderRadius:999 }}></span><span style={{ color:'#374151' }}>In Review</span></div>
                    </div>
                  </div>
        </div>

                <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:16 }}>
                  <h4 style={{ margin:'0 0 12px 0', color:'#374151' }}>Breakdown</h4>
                  {[
                    { label:'Approved', value: kybSummary.approved ?? 0, color:'#10b981' },
                    { label:'Pending', value: kybSummary.pending ?? 0, color:'#f59e0b' },
                    { label:'Rejected', value: kybSummary.rejected ?? 0, color:'#ef4444' },
                    { label:'In Review', value: kybSummary.inReview ?? 0, color:'#3b82f6' },
                  ].map((row) => (
                    <div key={row.label} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                      <div style={{ width:120, color:'#374151' }}>{row.label}</div>
                      <div style={{ flex:1, height:8, background:'#e5e7eb', borderRadius:6, position:'relative' }}>
                        <div style={{ width: `${(kybSummary.total ? (row.value / (kybSummary.total || 1)) * 100 : 0)}%`, height:8, background: row.color, borderRadius:6 }}></div>
                      </div>
                      <div style={{ width:24, textAlign:'right', color:'#374151' }}>{row.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : 'Generating...'}
        </div>
      )}
    </div>
  );
};

export default ReportsAnalytics; 